import React from "react";
import * as Sentry from "@sentry/browser";
import { BrowserStorageRequired } from "./BrowserStorageRequired/BrowserStorageRequired";
import { SomethingWentWrong } from "./SomethingWentWrong/SomethingWentWrong";

interface GlobalErrorBoundaryProps {
  children?: React.ReactNode;
}

interface GlobalErrorBoundaryState {
  hasError: boolean;
  isStorageError: boolean;
}

export class GlobalErrorBoundary extends React.Component<
  GlobalErrorBoundaryProps,
  GlobalErrorBoundaryState
> {
  state: GlobalErrorBoundaryState = {
    hasError: false,
    isStorageError: false,
  };

  static getDerivedStateFromError(error: unknown): Partial<GlobalErrorBoundaryState> {
    const isError = error instanceof Error;
    const isStorage =
      isError && (error.name === "SecurityError" || error.message?.includes("localStorage"));

    // Always return state, but mark if it's storage error
    return {
      hasError: true,
      isStorageError: Boolean(isStorage),
    };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo): void {
    const isError = error instanceof Error;
    const isStorage =
      isError && (error.name === "SecurityError" || error.message?.includes("localStorage"));

    if (isStorage) {
      return;
    }

    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  render(): React.ReactNode {
    if (this.state.hasError && this.state.isStorageError) {
      return <BrowserStorageRequired />;
    }

    // For non-storage errors, don't render children to prevent infinite loops
    // The error has been sent to Sentry, and state will be reset asynchronously
    if (this.state.hasError && !this.state.isStorageError) {
      return <SomethingWentWrong />;
    }

    return this.props.children;
  }
}
